import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowRight, Mail, Calendar, Shield, Send, CheckCircle2,
  Clock, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: '' // Hidden field for bots
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Math captcha state
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0 });
  const [mathAnswer, setMathAnswer] = useState('');
  
  // Generate random math question on mount
  React.useEffect(() => {
    generateMathQuestion();
  }, []);
  
  const generateMathQuestion = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathQuestion({ num1, num2 });
    setMathAnswer('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if filled, it's a bot
    if (formData.honeypot) {
      console.log('Bot detected via honeypot');
      setIsSubmitted(true); // Fake success to not alert the bot
      return;
    }
    
    // Math captcha validation
    const correctAnswer = mathQuestion.num1 + mathQuestion.num2;
    if (parseInt(mathAnswer) !== correctAnswer) {
      toast.error('Incorrect answer to the math question. Please try again.');
      generateMathQuestion();
      return;
    }
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const templateParams = {
        form_type: 'Contact Form',
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject || 'General Inquiry',
        message: formData.message || 'No message provided',
        to_email: 'brent@ipfunders.com'
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setIsSubmitted(true);
      toast.success('Message sent successfully');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-20">
        <div className="max-w-md mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#0f1f2e] mb-4">
            Message Received
          </h1>
          <p className="text-slate-600 mb-8">
            We'll respond within 1-2 business days. All communications are handled confidentially.
          </p>
          <Link to="/">
            <Button variant="outline" className="border-slate-300">
              Return Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-12">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-20 w-64 h-64 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f1f2e] mb-6">
            Get in Touch
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Have questions about IP risk-shifting? Ready to submit a concept for evaluation? We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#1e3a5f]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-[#1e3a5f]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2">Email Us</h3>
                <p className="text-slate-500 text-sm mb-4">For general inquiries</p>
                <p className="text-[#1e3a5f] font-medium">contact@ipfunders.com</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2">Schedule a Call</h3>
                <p className="text-slate-500 text-sm mb-4">Book an introductory conversation</p>
                <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white">
                  Book Time
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-slate-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2">Response Time</h3>
                <p className="text-slate-500 text-sm mb-4">We respond within 1-2 business days</p>
                <p className="text-slate-600 font-medium">Mon - Fri, 9am - 6pm EST</p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-[#0f1f2e] mb-4">
                Send Us a Message
              </h2>
              <p className="text-slate-600 mb-6">
                Whether you have questions about our process, want to explore partnership opportunities, or are ready to submit a concept — we're here to help.
              </p>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  <span className="text-slate-600">All communications are confidential</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                  <span className="text-slate-600">No unsolicited follow-ups</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  <span className="text-slate-600">Quick response within 1-2 business days</span>
                </div>
              </div>
            </div>

            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#0f1f2e] font-medium">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#0f1f2e] font-medium">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-[#0f1f2e] font-medium">
                      Subject
                    </Label>
                    <Input
                      id="subject"
                      type="text"
                      placeholder="How can we help?"
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#0f1f2e] font-medium">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us more about your inquiry..."
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="min-h-[120px] border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                    />
                  </div>

                  {/* Honeypot field - hidden from humans */}
                  <div className="absolute left-[-9999px]" aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.honeypot}
                      onChange={(e) => handleInputChange('honeypot', e.target.value)}
                    />
                  </div>

                  {/* Math Captcha */}
                  <div className="space-y-2">
                    <Label htmlFor="mathAnswer" className="text-[#0f1f2e] font-medium">
                      Security Check <span className="text-red-500">*</span>
                    </Label>
                    <div className="flex items-center space-x-3">
                      <span className="text-slate-700 font-medium bg-slate-100 px-4 py-3 rounded-lg">
                        {mathQuestion.num1} + {mathQuestion.num2} =
                      </span>
                      <Input
                        id="mathAnswer"
                        type="number"
                        placeholder="?"
                        value={mathAnswer}
                        onChange={(e) => setMathAnswer(e.target.value)}
                        className="h-12 w-24 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] text-center"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-[#1e3a5f] hover:bg-[#2a4a73] text-white text-base font-semibold shadow-lg shadow-[#1e3a5f]/20 transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#0f1f2e] mb-4">
            Ready to Submit a Concept?
          </h2>
          <p className="text-slate-600 mb-8">
            If you're ready to explore IP funding, start with an evaluation request.
          </p>
          <Link to="/request-access">
            <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2a4a73] text-white px-8 py-6 text-base shadow-lg shadow-[#1e3a5f]/20">
              Request Evaluation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
